<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import FormInput from '../../../form/input.vue' // Pfad anpassen
import { compareSizeOptions, normalizeSizeForSort } from '../../../../utils/sizeSorting'
type SizeOption = {
  id: string
  variantId?: string
  title: string
  price: number
  priceSpecial?: number
  regularPrice?: number | null
  featured?: boolean
  isDefault?: boolean
}

const props = defineProps<{
  sizeOptions: SizeOption[]
  groupDescription?: string
  imageMeta?: { widthPx:number; heightPx:number; aspectRatio:number } | null
  qualityDpi?: number
  limits?: { minW?: number; minH?: number; maxW?: number; maxH?: number }
  allowCustom?: boolean
  livePriceLabel?: string
  livePriceLoading?: boolean
  diffs?: Record<string, number>

  /** ✅ NEU: Sidebar kann eine Größe (Titel) vorgeben, ohne DB-Write */
  preselectTitle?: string | null
  /** ✅ default: "always" -> wenn Sidebar was vorgibt, setze es */
  preselectStrategy?: 'always' | 'if-empty'
}>()

const emit = defineEmits<{
  (e: 'changeSize', w:number, h:number, displayTitle?:string, sizeOptionId?: string): void
  (e: 'previewSize', w:number, h:number, displayTitle?:string): void
  (e: 'size-clicked'): void
}>()

/* ---------- Utils ---------- */
const STEP_CM = 0.1
const isNum = (v:any): v is number => typeof v === 'number' && Number.isFinite(v)
const roundStepDown = (v:number, step=STEP_CM) => Math.floor(v / step) * step
const roundStep     = (v:number, step=STEP_CM) => Math.round(v / step) * step
const toInches = (cm:number) => cm / 2.54
const dpiTarget = computed(() => props.qualityDpi ?? 180)
const nf = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' })
const deltas = computed(() => props.diffs ?? {})

/** "30 x 45 cm" -> {w,h} */
const parseFormat = (title:string) => {
  const cleaned = title?.replace(/cm/ig,'').trim()
  const m = cleaned.match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i)
  if (!m) return null
  const w = Number(m[1].replace(',','.'))
  const h = Number(m[2].replace(',','.'))
  if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return null
  return { w, h }
}

/* ---------- Rating ---------- */
function qualityDotsFromDpi(dpi:number, targetDpi:number): 0|1|2|3|4 {
  if (!isFinite(dpi) || dpi <= 0) return 0
  const target = Math.max(1, Number(targetDpi) || 180)
  const ratio = dpi / target
  if (ratio >= 1.0) return 4
  if (ratio >= 0.85) return 3
  if (ratio >= 0.70) return 2
  if (ratio >= 0.55) return 1
  return 0
}

/* ---------- State ---------- */
const selectedFormat = defineModel<string>('selectedFormat', { default: '' })
const selectedPrice  = defineModel<number>('selectedPrice',  { default: 0 })

const activeTab = ref<'standard'|'perfect'>('standard')
const customEnabled = ref(false)

const customWidth  = ref<number>(50)
const customHeight = ref<number>(75)
const customPreviewTitle = computed(() => `${Math.max(1, roundStep(Number(customWidth.value) || 1))} x ${Math.max(1, roundStep(Number(customHeight.value) || 1))} cm`)
const customLivePriceText = computed(() => {
  const label = String(props.livePriceLabel || '').trim()
  if (label) return label
  if (props.livePriceLoading) return 'Preis wird berechnet…'
  return 'Preis aktuell nicht verfügbar'
})
const customOrientationHint = computed(() => 'Geben Sie das Endformat des Produkts ein. Breite ist die waagerechte Seite, Höhe die senkrechte Seite.')
const customOrientationExamples = computed(() => [
  'Querformat: z. B. 30 cm breit und 20 cm hoch.',
  'Hochformat: z. B. 20 cm breit und 30 cm hoch.',
])
const customLimitHint = computed(() => {
  const { minW, minH, maxW, maxH } = props.limits || {}
  const widthParts: string[] = []
  const heightParts: string[] = []

  if (isNum(minW) && isNum(maxW)) widthParts.push(`${Number(minW)} bis ${Number(maxW)} cm`)
  else if (isNum(minW)) widthParts.push(`ab ${Number(minW)} cm`)
  else if (isNum(maxW)) widthParts.push(`bis ${Number(maxW)} cm`)

  if (isNum(minH) && isNum(maxH)) heightParts.push(`${Number(minH)} bis ${Number(maxH)} cm`)
  else if (isNum(minH)) heightParts.push(`ab ${Number(minH)} cm`)
  else if (isNum(maxH)) heightParts.push(`bis ${Number(maxH)} cm`)

  const lines: string[] = []
  if (widthParts.length) lines.push(`Breite erlaubt: ${widthParts.join('')}`)
  if (heightParts.length) lines.push(`Höhe erlaubt: ${heightParts.join('')}`)
  return lines
})
function setCustomFrom(w:number, h:number) {
  customWidth.value  = Math.max(1, roundStep(w))
  customHeight.value = Math.max(1, roundStep(h))
}

/* ---------- Ratio filter (wie bei dir) ---------- */
const RATIO_BUCKETS = [
  { label: 'Alle', value: 'ALL' },
  { label: '1:1',  value: 1/1 },
  { label: '2:3',  value: 2/3 },
  { label: '3:4',  value: 3/4 },
  { label: '4:5',  value: 4/5 },
  { label: '5:7',  value: 5/7 },
  { label: '3:2',  value: 3/2 },
  { label: '4:3',  value: 4/3 },
  { label: '5:4',  value: 5/4 },
  { label: '16:9', value: 16/9 },
  { label: '9:16', value: 9/16 },
  { label: 'Sonstige', value: 'OTHER' },
] as const
const RATIO_TOL = 0.03

type RatedSize = {
  id: string
  format: string
  variantId?: string
  width: number
  height: number
  price: number
  priceSpecial?: number
  qualityDots: 0|1|2|3|4
  dpi: number | null
  arDeltaPct: number
  recommended: boolean
  isDefault: boolean
}

/* ---------- Größen aufbereiten ---------- */
function rateSize(opt: SizeOption): RatedSize | null {
  const parsed = parseFormat(opt.title)
  const normalized = normalizeSizeForSort(opt)
  const widthMm = Number(normalized.widthMm || 0)
  const heightMm = Number(normalized.heightMm || 0)
  const w = parsed?.w ?? (widthMm > 0 ? widthMm / 10 : 0)
  const h = parsed?.h ?? (heightMm > 0 ? heightMm / 10 : 0)
  if (!(w > 0) || !(h > 0)) return null
  const arFormat = w / h
  const arImage  = props.imageMeta?.aspectRatio ?? null
  const arDeltaPct = arImage ? Math.abs(arFormat - arImage) / arImage * 100 : 0

  let dpi: number | null = null
  if (props.imageMeta?.widthPx && props.imageMeta?.heightPx) {
    const dpiW = props.imageMeta.widthPx  / toInches(w)
    const dpiH = props.imageMeta.heightPx / toInches(h)
    const raw = Math.min(dpiW, dpiH)
    dpi = Number.isFinite(raw) ? Math.round(raw) : null
  }
  const dots = qualityDotsFromDpi(Number(dpi || 0), dpiTarget.value)
  const recommended = (arDeltaPct <= 5) && (dpi == null || dots >= 3)

  return {
    id: opt.id,
    format: opt.title,
    width: w,
    height: h,
    // Bootstrap liefert price = effektiver Preis und regularPrice bei Aktion: fuer die
    // Durchstreich-Anzeige auf price/priceSpecial abbilden.
    price: opt.regularPrice ?? opt.price,
    variantId: opt.variantId,
    priceSpecial: opt.regularPrice != null ? opt.price : opt.priceSpecial,
    qualityDots: dots,
    dpi,
    arDeltaPct: Math.round(arDeltaPct * 10) / 10,
    recommended,
    isDefault: !!opt.isDefault
  }
}

const ratedSizes = computed(() => {
  const sorted = [...(props.sizeOptions ?? [])].sort(compareSizeOptions)
  return sorted.map(rateSize).filter(Boolean) as RatedSize[]
})

/** ✅ NEU: Hilfsfunktion um NUR UI zu setzen (ohne emit) */
function applyTitleToUiOnly(title: string) {
  if (!title) return

  // 1) wenn Titel existiert: set selectedFormat
  selectedFormat.value = title

  // 2) Preis aus sizeOptions matchen (wenn möglich)
  const match = props.sizeOptions?.find(o => o.title === title) || null
  if (match) selectedPrice.value = (match.priceSpecial ?? match.price) ?? selectedPrice.value

  // 3) Custom felder füllen, wenn parsebar
  const p = parseFormat(title)
  if (p) setCustomFrom(p.w, p.h)
}

/** ✅ NEU: Preselect von Sidebar übernehmen */
watch(
    () => [props.preselectTitle, props.preselectStrategy] as const,
    ([title, strategy]) => {
      const t = String(title || '').trim()
      if (!t) return
      const want = strategy ?? 'always'
      if (want === 'if-empty' && selectedFormat.value) return
      if (selectedFormat.value === t) return
      applyTitleToUiOnly(t)
    },
    { immediate: true },
)

/** Default-Auswahl, nur wenn nix gesetzt & kein Preselect */
watch(ratedSizes, (arr) => {
  if (!arr.length) return
  const hasPreselect = !!String(props.preselectTitle || '').trim()
  if (hasPreselect) return

  const def = arr.find(s => s.isDefault) || arr.find(s => s.recommended) || arr[0]
  if (!selectedFormat.value) {
    selectedFormat.value = def.format
    selectedPrice.value  = (def.priceSpecial ?? def.price)
    setCustomFrom(def.width, def.height)
    emit('changeSize', def.width, def.height, def.format,  def.variantId)
  }
}, { immediate: true })

/* Ratio dropdown (wie bei dir) */
const ratioOpen = ref(false)
const ratioSelected = ref<typeof RATIO_BUCKETS[number]['value']>('ALL')
const NUMERIC_BUCKETS = RATIO_BUCKETS.filter(
    (b): b is {label:string; value:number} => typeof b.value === 'number'
)

const bucketCount = computed(() => {
  const counts = new Map<string|number, number>()
  counts.set('ALL', ratedSizes.value.length)
  let other = 0
  for (const s of ratedSizes.value) {
    const ar = s.width / s.height
    let matched = false
    for (const b of NUMERIC_BUCKETS) {
      if (Math.abs(ar - b.value) / b.value <= RATIO_TOL) {
        counts.set(b.value, (counts.get(b.value) || 0) + 1)
        matched = true
      }
    }
    if (!matched) other++
  }
  if (other > 0) counts.set('OTHER', other)
  return counts
})

const ratioMenuOptions = computed(() => {
  const out: Array<{label:string; value:any; count:number}> = []
  out.push({ label: 'Alle', value: 'ALL', count: bucketCount.value.get('ALL') || 0 })
  for (const b of NUMERIC_BUCKETS) {
    const count = bucketCount.value.get(b.value) || 0
    if (count > 0) out.push({ label: b.label, value: b.value, count })
  }
  const otherCount = bucketCount.value.get('OTHER') || 0
  if (otherCount > 0) out.push({ label: 'Sonstige', value: 'OTHER', count: otherCount })
  return out
})

watch(ratioMenuOptions, (opts) => {
  if (ratioSelected.value !== 'ALL' && !opts.some(o => o.value === ratioSelected.value)) {
    ratioSelected.value = 'ALL'
  }
})
const labelForRatio = (val: typeof ratioSelected.value) =>
    (ratioMenuOptions.value.find(o => o.value === val)?.label) ?? 'Alle'

function matchesBucket(w:number, h:number, bucket: typeof ratioSelected.value) {
  if (bucket === 'ALL') return true
  const ar = w/h
  if (bucket === 'OTHER') {
    return !RATIO_BUCKETS.some(b => typeof b.value === 'number' && Math.abs(ar - b.value)/b.value <= RATIO_TOL)
  }
  return typeof bucket === 'number' && Math.abs(ar - bucket)/bucket <= RATIO_TOL
}

const filteredSizes = computed(() => {
  if (ratioSelected.value === 'ALL') return ratedSizes.value
  return ratedSizes.value.filter(s => matchesBucket(s.width, s.height, ratioSelected.value))
})

/* Auswahl & Custom (emitten, weil User-Aktion) */
function select(item: RatedSize) {
  selectedFormat.value = item.format
  selectedPrice.value  = (item.priceSpecial ?? item.price)
  setCustomFrom(item.width, item.height)
  emit('changeSize', item.width, item.height, item.format, item.variantId) // ✅ + id
  emit('size-clicked')
}

function applyCustom() {
  const w = Math.max(1, roundStep(Number(customWidth.value) || 1))
  const h = Math.max(1, roundStep(Number(customHeight.value) || 1))
  if (!customSizeValid.value) return
  const str = customPreviewTitle.value
  selectedFormat.value = str
  selectedPrice.value  = 0
  emit('changeSize', w, h, str)
  emit('size-clicked')
}

const customErrors = computed<string[]>(() => {
  const out: string[] = []
  const w = Math.max(0, Number(customWidth.value) || 0)
  const h = Math.max(0, Number(customHeight.value) || 0)
  const { minW, minH, maxW, maxH } = props.limits || {}
  const widthTooSmall = isNum(minW) && w < Number(minW)
  const widthTooLarge = isNum(maxW) && w > Number(maxW)
  const heightTooSmall = isNum(minH) && h < Number(minH)
  const heightTooLarge = isNum(maxH) && h > Number(maxH)

  if (widthTooSmall || widthTooLarge || heightTooSmall || heightTooLarge) {
    const reasons: string[] = []
    if (widthTooSmall) reasons.push(`die Breite unter ${Number(minW)} cm liegt`)
    else if (widthTooLarge) reasons.push(`die Breite über ${Number(maxW)} cm liegt`)
    if (heightTooSmall) reasons.push(`die Höhe unter ${Number(minH)} cm liegt`)
    else if (heightTooLarge) reasons.push(`die Höhe über ${Number(maxH)} cm liegt`)
    out.push(`Diese Größe ist für dieses Produkt leider nicht möglich, weil ${reasons.join(' und ')}.`)
  }
  return out
})
const customSizeValid = computed(() => customErrors.value.length === 0)

/* ---------- Perfect Fit (wie bei dir) ---------- */
type PFSuggestion = { w:number; h:number; dpi:number; note?:string }

function computePerfectFit(): PFSuggestion | null {
  const meta = props.imageMeta
  if (!meta?.widthPx || !meta?.heightPx) return null

  const ar = meta.widthPx / meta.heightPx
  const target = dpiTarget.value

  const maxWcmByDpi = (meta.widthPx  / target) * 2.54
  const maxHcmByDpi = (meta.heightPx / target) * 2.54
  const { minW, minH, maxW, maxH } = props.limits || {}
  const minWcm = isNum(minW) ? minW! : 0
  const minHcm = isNum(minH) ? minH! : 0
  const boxMaxW = isNum(maxW) ? Math.min(maxW!, maxWcmByDpi) : maxWcmByDpi
  const boxMaxH = isNum(maxH) ? Math.min(maxH!, maxHcmByDpi) : maxHcmByDpi

  let w = boxMaxW, h = w / ar
  if (h > boxMaxH) { h = boxMaxH; w = h * ar }

  if (isNum(minW) && w < minWcm) { w = minWcm; h = w / ar }
  if (isNum(minH) && h < minHcm) { h = minHcm; w = h * ar }

  w = Math.max(1, roundStep(w))
  h = Math.max(1, roundStep(h))
  if (w > boxMaxW || h > boxMaxH) {
    w = roundStepDown(Math.min(w, boxMaxW))
    h = roundStepDown(w / ar)
    if (h > boxMaxH) { h = roundStepDown(Math.min(h, boxMaxH)); w = roundStepDown(h * ar) }
  }

  const dpiW = meta.widthPx  / toInches(w)
  const dpiH = meta.heightPx / toInches(h)
  const dpi  = Math.round(Math.min(dpiW, dpiH))

  const note = dpi < dpiTarget.value ? 'DPI-Ziel wird leicht unterschritten' : undefined
  return { w, h, dpi, note }
}

const pfSuggestion = computed(() => computePerfectFit())

async function applyPerfectFitFromPreview() {
  const pf = pfSuggestion.value
  if (!pf) return

  setCustomFrom(pf.w, pf.h)

  const str = `${pf.w} x ${pf.h} cm`
  selectedFormat.value = str
  selectedPrice.value  = 0
  emit('changeSize', pf.w, pf.h, str)

  await nextTick()
  activeTab.value = 'custom'
}

/* Δ-Preis */
const keyOf = (o: {id?:string, title?:string}) => o.id || o.title || ''
const deltaFor = (o: {id?:string, title?:string}) => {
  const k = keyOf(o)
  return (k && typeof deltas.value[k] === 'number') ? deltas.value[k] : undefined
}
const deltaClass = (v?: number) => v == null ? '' : v > 0 ? 'up' : v < 0 ? 'down' : 'flat'
const deltaText  = (v?: number) => v == null ? '' : `${v > 0 ? '+' : v < 0 ? '−' : '±'}${nf.format(Math.abs(v))}`

/* Dropdown outside click */
function onDocClick(e: MouseEvent) {
  const t = e.target as HTMLElement | null
  if (!t) return
  if (t.closest('.ratio')) return
  ratioOpen.value = false
}
onMounted(() => document.addEventListener('click', onDocClick, { capture: true }))
onBeforeUnmount(() => document.removeEventListener('click', onDocClick, { capture: true }))

/* Sync Custom-Felder wenn selectedFormat parsebar */
watch(selectedFormat, (fmt) => {
  const p = fmt ? parseFormat(fmt) : null
  if (p) setCustomFrom(p.w, p.h)
})

watch([customWidth, customHeight], ([w, h]) => {
  const sw = Number(w)
  const sh = Number(h)
  if (Number.isFinite(sw) && Math.abs(sw - roundStep(sw)) > 0.0001) customWidth.value = roundStep(sw)
  if (Number.isFinite(sh) && Math.abs(sh - roundStep(sh)) > 0.0001) customHeight.value = roundStep(sh)
})

watch(activeTab, async (tab) => {
  if (tab === 'standard' && customEnabled.value) {
    await nextTick()
    const p = selectedFormat.value ? parseFormat(selectedFormat.value) : null
    if (p) setCustomFrom(p.w, p.h)
  }
})
watch(() => props.allowCustom, (v) => {
  if (v === false) customEnabled.value = false
}, { immediate: true })

watch([customEnabled, customWidth, customHeight, customSizeValid], ([enabled]) => {
  if (!enabled || !props.allowCustom || !customSizeValid.value) return
  const w = Math.max(1, roundStep(Number(customWidth.value) || 1))
  const h = Math.max(1, roundStep(Number(customHeight.value) || 1))
  emit('previewSize', w, h, customPreviewTitle.value)
}, { immediate: false })

watch(
  () => selectedFormat.value,
  (fmt) => {
    if (!props.allowCustom) {
      customEnabled.value = false
      return
    }
    const parsed = parseFormat(fmt)
    const isPreset = !!(props.sizeOptions || []).some((o) => String(o?.title || '') === String(fmt || ''))
    customEnabled.value = !!parsed && !isPreset
  },
  { immediate: true },
)

function toggleCustomEnabled() {
  if (!props.allowCustom) return
  customEnabled.value = !customEnabled.value
  if (customEnabled.value) {
    const p = selectedFormat.value ? parseFormat(selectedFormat.value) : null
    if (p) setCustomFrom(p.w, p.h)
    emit('previewSize', Math.max(1, roundStep(Number(customWidth.value) || 1)), Math.max(1, roundStep(Number(customHeight.value) || 1)), customPreviewTitle.value)
  }
}
</script>

<template>
  <!-- dein Template + CSS unverändert -->
  <!-- (genau wie bei dir gepostet) -->
  <section class="sizes">
    <div class="intro">
      <h3>Bildgröße wählen</h3>
      <p v-if="groupDescription" class="desc" v-html="groupDescription" />
    </div>

    <nav class="tabs tabs--two" role="tablist" aria-label="Größen">
      <button role="tab" :aria-selected="activeTab==='standard'"
              :class="{active: activeTab==='standard'}"
              @click="activeTab='standard'">Standard</button>
      <button role="tab" :aria-selected="activeTab==='perfect'"
              :class="{active: activeTab==='perfect'}"
              @click="activeTab='perfect'">Perfect&nbsp;Fit</button>
    </nav>

    <div v-show="activeTab==='perfect'" class="tab">
      <div class="pf-callout card">
        <div class="pf-text">
          <strong>Automatische Empfehlung</strong>
          <span>Wir berechnen das größtmögliche Format ohne Beschnitt bei optimaler Druckqualität.</span>
          <small>Produktlimits und DPI-Ziel (≥ {{ dpiTarget }} dpi) werden berücksichtigt.</small>
        </div>
        <div v-if="pfSuggestion" class="pf-preview">
          <div class="pf-line">
            Empfohlen: <strong>{{ pfSuggestion.w }} × {{ pfSuggestion.h }} cm</strong>
            <span class="pf-dpi">≈ {{ pfSuggestion.dpi }} dpi</span>
          </div>
          <small v-if="pfSuggestion.note" class="pf-note">⚠ {{ pfSuggestion.note }}</small>
          <button class="btn primary sm" @click="applyPerfectFitFromPreview">
            Jetzt passendes Format
          </button>
        </div>
        <div v-else class="pf-preview pf-muted">Kein Vorschaubild vorhanden.</div>
      </div>
    </div>

    <div v-show="activeTab==='standard'" class="tab">
      <div v-if="props.allowCustom" class="custom-toggle-card" :class="{ active: customEnabled }">
        <label class="custom-toggle">
          <input type="checkbox" :checked="customEnabled" @change="toggleCustomEnabled" />
          <span class="custom-toggle__box" aria-hidden="true"></span>
          <span class="custom-toggle__text">
            <strong>Individuelle Größe</strong>
          <small>Eigene Maße in 0,1-cm-Schritten eingeben.</small>
          </span>
        </label>
      </div>

        <div v-if="props.allowCustom && customEnabled" class="custom card custom-card">
        <div class="custom-help">
          <strong>Eigene Maße eingeben</strong>
          <span>{{ customOrientationHint }}</span>
          <small v-for="line in customOrientationExamples" :key="line">{{ line }}</small>
        </div>
        <div class="grid">
          <form-input label="Breite links nach rechts (cm)" v-model.number="customWidth" min="1" :step="STEP_CM" />
          <form-input label="Hoehe oben nach unten (cm)"   v-model.number="customHeight" min="1" :step="STEP_CM" />
        </div>
        <div class="custom-live">
          <strong>{{ customPreviewTitle }}</strong>
          <span class="custom-live__price" :data-loading="props.livePriceLoading ? '1' : '0'">{{ customLivePriceText }}</span>
        </div>
        <div v-if="customLimitHint.length" class="custom-limits">
          <span v-for="line in customLimitHint" :key="line">{{ line }}</span>
        </div>
        <div v-if="customErrors.length" class="custom-errors">
          <span v-for="err in customErrors" :key="err">{{ err }}</span>
        </div>
        <div class="right">
          <button class="btn" :disabled="!customSizeValid" @click="applyCustom">Übernehmen</button>
        </div>
      </div>

      <div v-if="!customEnabled" class="toolbar">
        <div class="ratio">
          <button class="ratio-btn" @click.stop="ratioOpen = !ratioOpen">
            <svg viewBox="0 0 24 24" class="ratio-ic" aria-hidden="true">
              <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>{{ labelForRatio(ratioSelected) }}</span>
          </button>

          <div v-if="ratioOpen" class="ratio-menu">
            <button
                v-for="o in ratioMenuOptions"
                :key="o.label"
                class="ratio-opt"
                :class="{ active: ratioSelected === o.value }"
                @click.stop="ratioSelected = o.value; ratioOpen = false"
            >
              {{ o.label }} <span class="count">({{ o.count }})</span>
            </button>
          </div>
        </div>
      </div>

      <div v-if="!customEnabled" class="list-scroll">
        <div class="list" v-if="filteredSizes.length">
          <button v-for="item in filteredSizes" :key="item.id" type="button"
                  class="row" :class="{ selected: selectedFormat === item.format }"
                  @click="select(item)">
            <div class="info">
              <div class="name">
                {{ item.format }}
                <span v-if="item.recommended" class="badge">Empfohlen</span>
              </div>
              <div class="sub">
                <span class="dots" :title="item.dpi ? `≈${item.dpi} dpi` : 'DPI unbekannt'">
                  <i v-for="n in 4" :key="n" class="dot" :class="{ on: n <= item.qualityDots }" />
                </span>
                <span class="dpi" aria-hidden="true">{{ item.dpi ? `≈ ${item.dpi} dpi` : 'DPI unbekannt' }}</span>
              </div>
            </div>
            <div class="price">
              <template v-if="item.priceSpecial && item.priceSpecial < item.price">
                <span class="old">{{ nf.format(item.price) }}</span>
                <span class="now">{{ nf.format(item.priceSpecial) }}</span>
              </template>
              <template v-else>
                <span class="now">{{ nf.format(item.price) }}</span>
              </template>
              <span v-if="deltaFor(item) !== undefined"
                    class="delta"
                    :class="deltaClass(deltaFor(item))">
                {{ deltaText(deltaFor(item)) }}
              </span>
            </div>
          </button>
        </div>
        <div v-else class="empty">Keine Größen für dieses Verhältnis.</div>
      </div>
    </div>
  </section>
</template>



<style scoped>
.sizes { padding: .5rem 0; color: var(--text); background: var(--bg); min-width: 0; }

/* Intro */
.intro h3 { margin: 0 0 .2rem 0; font-size: 18px; font-weight: 700; }
.desc { margin: 0 0 .55rem 0; color: var(--muted); font-size: 14px; }
.tabs--two { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.custom-toggle-card{
  margin: 0 0 .7rem 0;
  border: 1px solid color-mix(in srgb, var(--text) 10%, transparent);
  background: #fff;
  border-radius: 10px;
  padding: .8rem .9rem;
}
.custom-toggle-card.active{
  border-color: color-mix(in srgb, var(--primary) 40%, transparent);
  background: color-mix(in srgb, var(--primary) 5%, white);
}
.custom-toggle{
  display:flex;
  align-items:flex-start;
  gap:.75rem;
  cursor:pointer;
}
.custom-toggle input{
  position:absolute;
  opacity:0;
  pointer-events:none;
}
.custom-toggle__box{
  width:18px;
  height:18px;
  border-radius:4px;
  border:1px solid var(--border);
  background:#fff;
  flex:0 0 auto;
  margin-top: 2px;
  position:relative;
}
.custom-toggle input:checked + .custom-toggle__box{
  border-color: var(--primary);
  background: var(--primary);
}
.custom-toggle input:checked + .custom-toggle__box::after{
  content:'';
  position:absolute;
  left:5px;
  top:1px;
  width:5px;
  height:10px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.custom-toggle__text{
  display:grid;
  gap:.15rem;
}
.custom-toggle__text strong{
  font-size: 15px;
  font-weight:700;
}
.custom-toggle__text small{
  color: var(--muted);
  font-size: 13px;
}
.custom-live{
  display:flex;
  justify-content: space-between;
  align-items:center;
  gap:.75rem;
  margin-top:.65rem;
  padding:.6rem .75rem;
  border:1px solid color-mix(in srgb, var(--text) 10%, transparent);
  border-radius:6px;
  background: color-mix(in srgb, var(--bg) 86%, #f7f7f7);
  font-size: 14px;
}
.custom-live__price{
  white-space: nowrap;
  font-weight: 700;
}
.custom-live__price[data-loading="1"]{
  color: var(--muted);
}
.custom-help{
  display:grid;
  gap:.2rem;
  margin-bottom:.7rem;
  padding:.65rem .75rem;
  border:1px solid color-mix(in srgb, var(--text) 10%, transparent);
  border-radius:6px;
  background: color-mix(in srgb, var(--bg) 86%, #f7f7f7);
}
.custom-help strong{
  font-size: 14px;
}
.custom-help span{
  color: var(--muted);
  font-size: 13px;
}
.custom-help small{
  color: var(--muted);
  font-size: 12px;
  line-height: 1.4;
}
.custom-limits,
.custom-errors {
  display: grid;
  gap: .25rem;
  margin-top: .6rem;
  font-size: 13px;
}
.custom-limits { color: var(--muted); }
.custom-errors { color: #b42318; }

/* Tabs unter Beschreibung */
.tabs { display:grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap:.4rem; margin:.2rem 0 .7rem 0; }
.tabs button {
  appearance:none;
  height: 34px;
  padding: 0 .6rem;
  font-size: 14px;
  background:var(--bg-soft);
  border:1px solid var(--border);
  cursor:pointer;
  min-width: 0;
}
.tabs button.active { background: var(--primary); border-color: var(--primary); color:#fff; }

/* Perfect Fit */
.pf-callout.card {
  border: 1px solid #d8dee8;
  padding: .8rem;
  display: grid;
  gap: .55rem;
  background: #fff;
  box-shadow: none;
}
.pf-text strong {
  display:block;
  font-size: 15px;
}
.pf-text span, .pf-text small { color: var(--muted); }
.pf-preview { display:grid; gap:.5rem; }
.pf-line {
  display:flex;
  gap:.5rem;
  align-items:baseline;
  flex-wrap: wrap;
}
.pf-line strong {
  font-size: 15px;
}
.pf-dpi {
  color: #334155;
  font-size: 12px;
  border: 1px solid #cbd5e1;
  background: #fff;
  padding: 2px 8px;
}
.pf-note { color:#b25b00; }
.btn.primary.sm {
  height: 34px;
  padding:0 .7rem;
  border: 1px solid #0f172a;
  background: #0f172a;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
}

/* Custom */
.custom.card { border:1px solid var(--border); padding: .75rem; border-radius: 10px; background:#fff; }
.custom-card{ margin-bottom: .75rem; }
.custom .grid { display:grid; grid-template-columns:1fr 1fr; gap:.75rem; }
.custom .right { display:flex; justify-content:flex-end; margin-top:.5rem; }
.custom .btn { height: 34px; padding: 0 .8rem; font-size: 13px; }
:deep(.custom .form-group label){ font-size: 12px; margin-bottom: .2rem; }
:deep(.custom .form-control){
  width: 100%;
  height: 34px;
  border: 1px solid var(--border);
  padding: 0 .55rem;
  font-size: 14px;
  background: #fff;
}

/* Standard */
.toolbar { display:flex;  align-items:center; margin-bottom:.5rem;
  justify-content: flex-start; }
.ratio { position:relative; }
.ratio-btn {
  display:flex; align-items:center; gap:.35rem;
  height: 32px;
  border:1px solid var(--border);
  background:var(--bg);
  padding:0 .55rem;
  cursor:pointer;
  font-size: 13px;
}
.ratio-ic { width:16px; height:16px; }
.ratio-menu {
  position:absolute; right:0; top:calc(100% + 6px);
  min-width:160px; max-width: min(220px, 100%);
  background:#fff; border:1px solid var(--border); box-shadow:none; z-index:20; padding:.25rem;
}
.ratio-opt { width:100%; text-align:left; padding:.45rem .55rem; border:none; background:transparent; cursor:pointer; font-size: 13px; }
.ratio-opt:hover { background:#fafafa; }
.ratio-opt.active { background:var(--primary-10); }

/* Scrollbarer Bereich für Größenliste */
.list-scroll { max-height: clamp(190px, 36vh, 300px); overflow-y: auto; padding-right: 0; min-width: 0; }
.list { display:flex; flex-direction:column; gap:.5rem; }
.row { display:grid; grid-template-columns:minmax(0, 1fr) auto; align-items:center; gap:.45rem; padding:.5rem .55rem; border:1px solid var(--border); background:var(--bg); cursor:pointer; text-align:left; min-width: 0; }
.row:hover { box-shadow: none; background: #fafafa; }
.row.selected { border-color: var(--ok); box-shadow: 0 0 0 3px var(--ok-10); }

.info { display:grid; gap:.2rem; min-width: 0; }
.name { font-weight:600; display:flex; align-items:center; gap:.35rem; font-size: 14px; }
.badge { background:var(--ok-10); color:var(--ok); border:1px solid rgba(28,124,47,.35); padding:.05rem .35rem; border-radius:999px; font-size: 12px; }
.sub { display:flex; align-items:center; gap:.45rem; color: var(--muted); font-size: 12px; flex-wrap: wrap; }
.dots { display:inline-flex; gap:3px; }
.dot { width:8px; height:8px; border-radius:50%; background:#cfcfcf; }
.dot.on { background:#28a745; }
.dpi { opacity:.9; }

.price { display:flex; align-items:center; justify-content:flex-end; gap:.28rem; white-space:nowrap; min-width: 74px; font-size: 12px; }
.old { text-decoration:line-through; color:var(--muted-2); }
.now { font-weight: 700; font-size: 13px; }
.delta { font-weight: 700; margin-left:.2rem; font-size: 12px; }
.delta.up { color:#c73737; }
.delta.down { color:#2f8f2f; }
.delta.flat { color:#666; }

.empty { padding:.6rem; color: var(--muted); font-size: 14px; }

@media (max-width: 430px) {
  .custom .grid { grid-template-columns:1fr; }
  .price { min-width: 68px; }
}

/* Reduce motion */
@media (prefers-reduced-motion: reduce) {
  .row:hover { box-shadow:none; }
}
</style>
